import { X } from 'lucide-react'
import { Dialog as SheetPrimitive } from 'radix-ui'
import type { ComponentProps } from 'react'

import { cn } from '@/lib/utils'

/** A panel on the right on tablets and desktops, rising from the bottom on phones (USA-1345). */
function Sheet(props: ComponentProps<typeof SheetPrimitive.Root>) {
  return <SheetPrimitive.Root data-slot="sheet" {...props} />
}

function SheetContent({
  className,
  children,
  ...props
}: ComponentProps<typeof SheetPrimitive.Content>) {
  return (
    <SheetPrimitive.Portal>
      <SheetPrimitive.Overlay
        className="bg-usa-navy/25 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 duration-200"
        data-slot="sheet-overlay"
      />
      <SheetPrimitive.Content
        className={cn(
          'bg-card text-card-foreground fixed z-50 flex flex-col shadow-[0_-8px_30px_rgba(17,24,39,0.12)] outline-none',
          'data-[state=open]:animate-in data-[state=closed]:animate-out duration-200 ease-out data-[state=closed]:ease-in',
          'inset-x-0 bottom-0 max-h-[85dvh] rounded-t-xl data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom',
          'sm:inset-y-0 sm:right-0 sm:left-auto sm:max-h-none sm:w-[420px] sm:rounded-none sm:border-l sm:shadow-[-8px_0_30px_rgba(17,24,39,0.10)]',
          'sm:data-[state=closed]:slide-out-to-bottom-0 sm:data-[state=closed]:slide-out-to-right sm:data-[state=open]:slide-in-from-bottom-0 sm:data-[state=open]:slide-in-from-right',
          className,
        )}
        data-slot="sheet-content"
        {...props}
      >
        {children}
        <SheetPrimitive.Close
          aria-label="Close"
          className="text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-usa-blue absolute top-3 right-3 grid size-9 place-items-center rounded-md focus-visible:outline-2"
        >
          <X aria-hidden="true" className="size-4" />
        </SheetPrimitive.Close>
      </SheetPrimitive.Content>
    </SheetPrimitive.Portal>
  )
}

function SheetTitle({ className, ...props }: ComponentProps<typeof SheetPrimitive.Title>) {
  return (
    <SheetPrimitive.Title
      className={cn('text-usa-navy text-lg leading-snug font-extrabold', className)}
      data-slot="sheet-title"
      {...props}
    />
  )
}

export { Sheet, SheetContent, SheetTitle }
